import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Clock, Radio } from "lucide-react";
import Header from "@/components/Header";

// ── Cinematic background config (mirrors JoinTheBattle) ──────────────────────
const ALL_BG_AVATARS = [
  "/24-girl.png", "/25-girl.png", "/26-girl.png", "/34-girl.png", "/35-girl.png",
  "/36-girl.png", "/38-girl.png", "/27-boy.png",  "/28-boy.png",  "/29-boy.png",
  "/30-boy.png",  "/31-boy.png",  "/32-boy.png",  "/33-boy.png",  "/37-boy.png",
  "/fe-1.png",    "/fe-2.png",    "/fe-3.png",    "/fe-4.png",
  "/male-1.png",  "/male-2.png",  "/male-3.png",  "/male-4.png",
];
const BG_COLS = [
  { dir: "up",   speed: 22, delay: "0s",   opacity: 0.80, blur: 0, gs: 60, bri: 0.50 },
  { dir: "down", speed: 29, delay: "2.1s", opacity: 0.70, blur: 0, gs: 55, bri: 0.45 },
  { dir: "up",   speed: 17, delay: "0.5s", opacity: 0.55, blur: 0, gs: 52, bri: 0.40 },
  { dir: "down", speed: 33, delay: "1.1s", opacity: 0.38, blur: 1, gs: 48, bri: 0.38 },
  { dir: "up",   speed: 25, delay: "0s",   opacity: 0.22, blur: 3, gs: 42, bri: 0.32 },
  { dir: "down", speed: 20, delay: "1.6s", opacity: 0.22, blur: 3, gs: 42, bri: 0.32 },
  { dir: "up",   speed: 31, delay: "0.3s", opacity: 0.38, blur: 1, gs: 48, bri: 0.38 },
  { dir: "down", speed: 18, delay: "2.6s", opacity: 0.55, blur: 0, gs: 52, bri: 0.40 },
  { dir: "up",   speed: 26, delay: "0.8s", opacity: 0.70, blur: 0, gs: 55, bri: 0.45 },
  { dir: "down", speed: 23, delay: "1.3s", opacity: 0.80, blur: 0, gs: 60, bri: 0.50 },
];
function getBgCol(ci) {
  const imgs = [];
  for (let i = 0; i < 4; i++) imgs.push(ALL_BG_AVATARS[(ci * 3 + i) % ALL_BG_AVATARS.length]);
  return [...imgs, ...imgs];
}

export default function MockPending() {
  const navigate  = useNavigate();
  const [checking, setChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [countdown, setCountdown] = useState(15);
  const email = localStorage.getItem("mock_email") || "";
  const name  = localStorage.getItem("mock_name")  || "Candidate";

  async function checkStatus() {
    setChecking(true);
    try {
      const res = await api.mockApprovalStatus(email);
      setLastChecked(new Date());
      setCountdown(15);
      if (res.approved) {
        navigate("/mock/positions", { replace: true });
      } else if (res.rejected) {
        localStorage.removeItem("mock_token");
        navigate("/mock", { replace: true });
      }
    } catch (_) {}
    finally { setChecking(false); }
  }

  // Auto-poll every 15 s + live countdown
  useEffect(() => {
    if (!email) { navigate("/mock", { replace: true }); return; }
    checkStatus();
    const pollTimer = setInterval(checkStatus, 15000);
    const countTimer = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 15)), 1000);
    return () => { clearInterval(pollTimer); clearInterval(countTimer); };
  }, []);

  function handleLogout() {
    localStorage.removeItem("mock_token");
    localStorage.removeItem("mock_email");
    localStorage.removeItem("mock_name");
    navigate("/mock", { replace: true });
  }

  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      {/* ── Cinematic keyframes ── */}
      <style>{`
        @keyframes mp-up    { 0%{transform:translateY(0)}     100%{transform:translateY(-50%)} }
        @keyframes mp-down  { 0%{transform:translateY(-50%)}  100%{transform:translateY(0)} }
        @keyframes mp-ambi  { 0%,100%{opacity:0.10} 50%{opacity:0.26} }
        @keyframes mp-ambi2 { 0%,100%{opacity:0.06} 50%{opacity:0.18} }
        @keyframes mp-blink { 0%,100%{opacity:1} 50%{opacity:0.15} }
        @keyframes mp-ring  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes mp-ring2 { from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
        @keyframes mp-ping  { 0%{transform:scale(1);opacity:0.7} 100%{transform:scale(2.4);opacity:0} }
      `}</style>

      {/* ── Wall of Fighters background ── */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none" }}>
        {BG_COLS.map((col, ci) => (
          <div key={ci} style={{
            position: "absolute", left: `${ci * 10}%`, top: 0, bottom: 0, width: "10.4%",
            overflow: "hidden", opacity: col.opacity,
            filter: col.blur > 0 ? `blur(${col.blur}px)` : undefined,
          }}>
            <div style={{
              display: "flex", flexDirection: "column", gap: 6,
              animation: `${col.dir === "up" ? "mp-up" : "mp-down"} ${col.speed}s linear ${col.delay} infinite`,
            }}>
              {getBgCol(ci).map((src, ii) => (
                <img key={ii} src={src} alt="" style={{
                  width: "100%", height: "27vh", objectFit: "cover", objectPosition: "top center",
                  borderRadius: 8, flexShrink: 0,
                  filter: `grayscale(${col.gs}%) brightness(${col.bri}) contrast(1.1)`,
                }} />
              ))}
            </div>
          </div>
        ))}
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(90deg, rgba(6,10,31,0.88) 0%, transparent 16%, transparent 84%, rgba(6,10,31,0.88) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, zIndex: 1, background: "linear-gradient(180deg, rgba(6,10,31,0.90) 0%, transparent 14%, transparent 86%, rgba(6,10,31,0.90) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, zIndex: 2, background: "radial-gradient(ellipse 48% 80% at 50% 48%, rgba(6,10,31,0.92) 0%, rgba(6,10,31,0.70) 48%, transparent 100%)" }} />
        <div style={{ position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)", width: 800, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(230,57,70,0.12) 0%, transparent 65%)", animation: "mp-ambi 3.5s ease-in-out infinite", zIndex: 3 }} />
        <div style={{ position: "absolute", bottom: "-10%", left: "50%", transform: "translateX(-50%)", width: 700, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(230,57,70,0.08) 0%, transparent 65%)", animation: "mp-ambi2 4.5s ease-in-out 1s infinite", zIndex: 3 }} />
        <div style={{ position: "absolute", inset: 0, opacity: 0.035, zIndex: 2, backgroundImage: "linear-gradient(rgba(230,57,70,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(230,57,70,0.9) 1px, transparent 1px)", backgroundSize: "55px 55px" }} />
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 px-5 md:px-10 pt-6 pb-10 md:py-10">
        <div className="max-w-xl mx-auto">

          {/* Page label */}
          <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-2">
            <span className="kl-accent-line mr-3" />Proctorly AI · Mock Interviews
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.9] tracking-tight mb-6 md:mb-8">
            You're in.<br /><span className="text-[color:var(--kl-red)]">Almost.</span>
          </h1>

          {/* Waiting card */}
          <div className="rounded-xl border border-white/10 p-6 md:p-8 space-y-6"
            style={{ background: "rgba(6,10,31,0.75)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" }}>

            {/* Animated waiting indicator */}
            <div className="flex flex-col items-center gap-5 py-4">
              {/* Triple ring loader */}
              <div style={{ position: "relative", width: 88, height: 88 }}>
                {/* Outer ping */}
                <div style={{
                  position: "absolute", inset: 0, borderRadius: "50%",
                  border: "2px solid rgba(230,57,70,0.35)",
                  animation: "mp-ping 2s ease-out infinite",
                }} />
                {/* Outer ring */}
                <div style={{
                  position: "absolute", inset: 4, borderRadius: "50%",
                  border: "2px solid rgba(230,57,70,0.18)",
                  borderTopColor: "rgba(230,57,70,0.65)",
                  animation: "mp-ring 3s linear infinite",
                }} />
                {/* Inner ring */}
                <div style={{
                  position: "absolute", inset: 16, borderRadius: "50%",
                  border: "2px solid rgba(230,57,70,0.12)",
                  borderBottomColor: "#e63946",
                  animation: "mp-ring2 2s linear infinite",
                }} />
                {/* Centre dot */}
                <div style={{
                  position: "absolute", inset: 32, borderRadius: "50%",
                  background: "rgba(230,57,70,0.8)",
                  boxShadow: "0 0 14px rgba(230,57,70,0.9)",
                }} />
              </div>

              <div className="text-center space-y-1">
                <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-[color:var(--kl-red)] flex items-center gap-2 justify-center">
                  <span className="w-2 h-2 rounded-full bg-[color:var(--kl-red)] inline-block" style={{ animation: "mp-blink 1s ease-in-out infinite" }} />
                  Pending Admin Approval
                </div>
                <div className="font-display text-2xl uppercase text-white">{name}</div>
              </div>
            </div>

            {/* Status info box */}
            <div className="rounded-xl border border-yellow-400/25 p-5 flex items-start gap-4"
              style={{ background: "rgba(250,204,21,0.05)" }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(250,204,21,0.15)" }}>
                <Clock className="w-4 h-4 text-yellow-400" />
              </div>
              <div>
                <div className="font-mono-kl text-[10px] uppercase tracking-widest text-yellow-400 mb-1">Request Submitted</div>
                <p className="text-white/75 text-sm leading-relaxed">
                  Hi <strong className="text-white">{name}</strong>, your access request has been sent to the{" "}
                  <strong className="text-white">Kiba Labs admin</strong>. You'll be automatically redirected
                  as soon as your account is approved.
                </p>
              </div>
            </div>

            {/* Auto-check countdown */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white/30 font-mono-kl text-[9px]">
                <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                Next auto-check in {countdown}s
              </div>
              {lastChecked && (
                <div className="font-mono-kl text-[9px] text-white/25">
                  Last checked {lastChecked.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-1">
              <button
                onClick={checkStatus}
                disabled={checking}
                className="btn-submit disabled:opacity-40"
                style={{ flex: 1 }}
              >
                {checking
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Checking…</>
                  : <><Radio className="w-4 h-4 mr-2" />Check Now</>
                }
              </button>
              <button
                onClick={handleLogout}
                className="btn-ghost-dark"
                style={{ flex: 1, justifyContent: "center" }}
              >
                Sign Out
              </button>
            </div>
          </div>

          <p className="text-white/25 font-mono-kl text-[10px] uppercase tracking-widest text-center mt-5 leading-relaxed">
            Contact your training coordinator if you don't hear back within 24 hours.
          </p>
          <div className="mt-6 text-center text-white/30 font-mono-kl text-[10px] uppercase tracking-widest">
            Proctorly AI · Powered by Kiba Labs · AI Summit
          </div>
        </div>
      </div>
    </div>
  );
}
